import { useCallback, useEffect, useMemo } from 'react';

import type { Model } from '@snowit/types/firestore';
import type {
  ConfigModel,
  TenantGroups,
  TenantMessageModel,
  TenantModel,
} from '@snowit/types/tenants';

import * as cache from '@snowit/cache';
import { isNewEntity } from '@snowit/constants';
import useFirebase from '@snowit/firebase';
import useModals from '@snowit/hooks/useModals';
import { SchemaNames } from '@snowit/jsonschemas/__names';
import { useStore } from '@snowit/store';
import PERMISSIONS from '@snowit/utilities/permissions';
import random from '@snowit/utils/random';

import useAuth from '../useAuth';
import useLanguage from '../useLanguage';
import useSchemas from '../useSchemas';

export default (noMulti?: boolean) => {
  const firebase = useFirebase();
  const { te } = useLanguage();
  const { store, setStore } = useStore();
  const { authType, claims, hasPermission, user } = useAuth();
  const { writeDocument, writeSubDocument, deleteSubDocument } = useSchemas();
  const { showErrorV2 } = useModals();

  const tenants: TenantModel[] = useMemo(() => {
    return store.tenants.list.filter((a) => {
      if (authType === 'ADMIN') return true;
      if (hasPermission(PERMISSIONS.SELECTORS__TENANTS_VIEW_ALL)) return true;
      return (claims.tenants ?? []).includes(a.uid);
    });
  }, [store.tenants.list]);

  const active: TenantModel[] = useMemo(() => {
    return tenants.filter((it) => store.tenants.active.includes(it.uid));
  }, [tenants, store.tenants.active]);

  useEffect(() => {
    if (noMulti && active.length > 1) {
      showErrorV2({
        message: te('no_multi_tenant'),
        code: 'no_multi_tenant',
        stack: false,
        id: 'no-multi-tenant',
      });
    }
  }, [noMulti, active.length]);

  const syncConfigs = async (tenantUID: string) => {
    try {
      await firebase.call('tenants-publishTenantConfig', { tenant: tenantUID });
    } catch (error: any) {
      console.error('syncConfigs', { error });
    }
  };

  const getTenants = async (): Promise<TenantModel[]> => {
    try {
      const res = await firebase.query<TenantModel>({
        collectionName: 'tenants',
        filters: (q) => q.limit(0),
      });
      setStore('tenants', { list: res });
      return res;
    } catch (error) {
      setStore('tenants', { list: [] });
      console.error({ error });
      return [];
    } finally {
      setStore('tenants', { ready: true });
    }
  };

  const selectTenant = (uids?: string[]) => {
    if (!uids) {
      cache.removeLocal('tenants');
    } else {
      cache.setLocal<string[]>('tenants', uids);
    }
    setStore('tenants', { active: uids ?? [] });
  };

  const setBookmarks = (uids?: string[]) => {
    if (!uids) {
      cache.removeLocal('tenants_bookmarks');
    } else {
      cache.setLocal<string[]>('tenants_bookmarks', uids);
    }
    setStore('tenants', { bookmarks: uids ?? [] });
  };

  const getName = (uid: string): string => {
    const selected = tenants.find((tnt) => tnt.uid === uid);
    if (!selected) return uid;
    return selected.name;
  };

  const writeTenant = async (tenantUID: string, data: Partial<TenantModel>): Promise<boolean> => {
    try {
      if (isNewEntity(tenantUID)) return false;
      await writeDocument({
        id: tenantUID,
        modelName: SchemaNames.TENANT,
        attributes: data,
      });
      return true;
    } catch (error: any) {
      console.error('writeTenant', { error });
      return false;
    }
  };

  const writeConfig = async <T extends keyof ConfigModel>(
    tenantUID: string,
    key: T,
    data: ConfigModel[T],
  ) => {
    try {
      const privates: (keyof ConfigModel)[] = ['referrals', 'satispay'];
      if (isNewEntity(tenantUID)) return;
      await writeSubDocument({
        parentId: tenantUID,
        modelName: SchemaNames.TENANT_CONFIG,
        attributes: {
          ...data,
          private: privates.includes(key),
        },
        id: key,
      });
    } catch (error: any) {
      console.error('writeConfig', { error });
    }
  };

  const deleteTenantMessage = useCallback(async (tenantUID: string, uid: string) => {
    try {
      if (isNewEntity(tenantUID)) return;
      await deleteSubDocument({
        parentId: tenantUID,
        modelName: SchemaNames.TENANT_TENANTMESSAGE,
        id: uid,
      });
    } catch (error: any) {
      console.error('deleteTenantMessage', { error });
    }
  }, []);

  const updateTenant = async (obj) => {
    try {
      const res = await firebase.call('models-updateTenants', obj);
      return res;
    } catch (error: any) {
      console.error('deleteTenantMessage', { error });
    }
  };

  const writeTenantMessage = async (tenantUID: string, message: TenantMessageModel) => {
    try {
      if (isNewEntity(tenantUID)) return;
      const { uid, ...data } = message;
      await writeSubDocument({
        parentId: tenantUID,
        modelName: SchemaNames.TENANT_TENANTMESSAGE,
        attributes: {
          ...data,
          expireDate: Array.isArray(data.expireDate) ? data.expireDate[0] : data.expireDate,
        },
        id: uid,
      });
    } catch (error: any) {
      console.error('writeTenantMessage', { error });
    }
  };

  const getConfig = async (tenantUID: string): Promise<ConfigModel | undefined> => {
    try {
      const res = await firebase.subcollection<Model<any>>({
        collectionName: 'tenants',
        id: tenantUID,
        subcollectionName: 'config',
      });

      const obj: ConfigModel = res.reduce(
        (memo, entry) => ({
          ...memo,
          [entry.uid]: entry,
        }),
        {} as ConfigModel,
      );
      return obj;
    } catch (error: any) {
      console.error('getConfig', { error });
      return undefined;
    }
  };

  const getMessages = async (tenantUID: string): Promise<TenantMessageModel[]> => {
    try {
      const res = await firebase.subcollection<TenantMessageModel>({
        collectionName: 'tenants',
        id: tenantUID,
        subcollectionName: 'messages',
      });
      return res;
    } catch (error) {
      console.error({ error });
      return [];
    }
  };

  const getTenant = async (tenantUID: string): Promise<TenantModel | undefined> => {
    try {
      const res = await firebase.doc<TenantModel>({ collectionName: 'tenants', id: tenantUID });
      return res;
    } catch (error) {
      console.error({ error });
      return undefined;
    }
  };

  const isManager = useMemo(() => {
    if (authType === 'ADMIN') return true;
    if (authType === 'USER') return false;
    return ['all', ...active.map((it) => it.uid)]
      .filter(Boolean)
      .some((it) => (user?.tenantsManager ?? []).includes(it));
  }, [active, user, authType]);

  const isManagerButNotAll = useMemo(() => {
    if (authType === 'PARTNER') {
      if (!user?.tenantsManager?.length) return false;
      if (user.tenantsManager.includes('all')) return false;
      if (active.some((it) => user.tenantsManager?.includes(it.uid))) return false;
      return true;
    }
    return false;
  }, [authType, active, user]);

  const getTenantName = (input?: string): string => {
    if (!input) return '';
    if (input === 'default') return 'Default';
    return tenants.find((it) => it.uid === input)?.name ?? '';
  };

  const addGroup = (uids: string[]) => {
    const id = random.id();
    setStore('tenants', (prev) => {
      const newGroups = { ...prev.groups, [id]: uids };
      cache.setLocal<TenantGroups>('tenants_groups', newGroups);
      return {
        ...prev,
        groups: newGroups,
      };
    });
  };

  const removeGroup = (id: string) => {
    setStore('tenants', (prev) => {
      const prevGroups = { ...prev.groups };
      delete prevGroups[id];
      cache.setLocal<TenantGroups>('tenants_groups', prevGroups);
      return {
        ...prev,
        groups: prevGroups,
      };
    });
  };

  const hasTenants = useCallback(
    (input: string | string[] | null | undefined): boolean => {
      if (!input) return false;
      if (Array.isArray(input)) {
        if (!input.length) return false;
        return active.some((it) => input.includes(it.uid));
      }
      return active.some((it) => it.uid === input);
    },
    [active],
  );

  return {
    tenants,
    active,
    activeTenant: store.tenants.active[0],
    activeTenants: store.tenants.active,
    bookmarks: store.tenants.bookmarks,
    groups: store.tenants.groups,
    addGroup,
    removeGroup,
    getConfig,
    getMessages,
    getName,
    getTenants,
    selectTenant,
    setBookmarks,
    writeTenant,
    writeConfig,
    writeTenantMessage,
    deleteTenantMessage,
    syncConfigs,
    updateTenant,
    getTenant,
    getTenantName,
    hasTenants,
    isManager,
    isManagerButNotAll,
  };
};
